import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type { CountryCode } from '../providers/locale-provider';

/**
 * Validates a phone number.
 *
 * @param phoneNumber - The phone number to validate.
 * @param opts - Options for validation.
 * @param opts.callback - An optional callback function to be called after validation.
 * @param opts.countryCode - The country code for the phone number.
 * @param opts.required - Specifies if the phone number is required.
 * @returns A boolean indicating if the phone number is valid.
 */
export function validatePhoneNumber(
  phoneNumber: string | null | undefined,
  opts: {
    callback?: (err?: unknown) => void;
    countryCode: CountryCode;
    required?: boolean;
    requiredMessage?: string;
    invalidMessage?: string;
  },
): boolean {
  const {
    callback,
    countryCode,
    required,
    requiredMessage = 'Phone number is required',
    invalidMessage = 'Phone number is invalid',
  } = opts;

  try {
    const hasValue = phoneNumber && phoneNumber !== '';

    if (!hasValue) {
      if (required) {
        throw new Error(requiredMessage);
      }

      // eslint-disable-next-line callback-return -- return is present
      callback?.();
      return true;
    }

    // Special handling for Australian mobile numbers
    if (countryCode === 'AU') {
      // Remove any non-digit characters
      const cleanNumber = phoneNumber.replace(/\D/g, '');

      // Check if it's a valid Australian number format
      // Australian numbers can be:
      // - 04xxxxxxxx (10 digits, starts with 04) - Mobile
      // - 4xxxxxxxx (9 digits, starts with 4) - Mobile
      // - 1300xxxxxx (10 digits, starts with 1300) - Local rate service
      // - 1800xxxxxx (10 digits, starts with 1800) - Toll-free service
      if (
        (cleanNumber.startsWith('04') && cleanNumber.length === 10) ||
        (cleanNumber.startsWith('4') && cleanNumber.length === 9) ||
        (cleanNumber.startsWith('1300') && cleanNumber.length === 10) ||
        (cleanNumber.startsWith('1800') && cleanNumber.length === 10)
      ) {
        // eslint-disable-next-line callback-return -- return is present
        callback?.();
        return true;
      }
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, countryCode);
    if (!parsedPhoneNumber?.isValid()) {
      throw new Error(invalidMessage);
    }
  } catch (err: unknown) {
    // eslint-disable-next-line callback-return -- return is present
    callback?.(err);
    return false;
  }
  // eslint-disable-next-line callback-return -- return is present
  callback?.();
  return true;
}
